class CampHollowReviewCarousel extends HTMLElement {
  #backgroundHost;
  #backgrounds;
  #layers = [];
  #activeLayer = 0;
  #scroller;
  #gesture;
  #animationFrame;
  #originalTouchAction;
  #originalSnap;
  #touchStart = (event) => {
    if (event.touches.length !== 1) {
      this.#touchEnd();
      return;
    }
    this.#stopAnimation();
    const scroller = this.#scroller;
    const targets = this.#slideTargets();
    if (!scroller || targets.length < 2) return;
    const touch = event.touches[0];
    this.#gesture = {
      id: touch.identifier,
      x: touch.clientX,
      y: touch.clientY,
      delta: 0,
      axis: undefined,
      left: scroller.scrollLeft,
      targets,
      index: targets.reduce((nearest, value, index) =>
        Math.abs(value - scroller.scrollLeft) < Math.abs(targets[nearest] - scroller.scrollLeft) ? index : nearest, 0),
    };
  };
  #touchMove = (event) => {
    const gesture = this.#gesture;
    if (!gesture || event.touches.length !== 1) return;
    const touch = Array.from(event.touches).find((item) => item.identifier === gesture.id);
    if (!touch) return;
    const dx = gesture.x - touch.clientX;
    const dy = gesture.y - touch.clientY;
    if (!gesture.axis && Math.max(Math.abs(dx), Math.abs(dy)) >= 8) {
      gesture.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (gesture.axis !== 'x' || !event.cancelable) return;
    event.preventDefault();
    gesture.delta = dx;
    this.#scroller.style.scrollSnapType = 'none';
    this.#scroller.scrollTo({ left: gesture.left + dx, behavior: 'instant' });
  };
  #touchEnd = () => {
    const gesture = this.#gesture;
    this.#gesture = undefined;
    if (!gesture || gesture.axis !== 'x') {
      this.#restoreSnap();
      return;
    }
    const threshold = Math.min(40, this.#scroller.clientWidth * 0.12);
    const step = Math.abs(gesture.delta) >= threshold ? Math.sign(gesture.delta) : 0;
    const index = Math.max(0, Math.min(gesture.targets.length - 1, gesture.index + step));
    this.#animateTo(gesture.targets[index]);
  };
  #slideTargets() {
    const scroller = this.#scroller;
    if (!scroller) return [];
    const left = scroller.getBoundingClientRect().left + scroller.clientLeft;
    const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    return Array.from(scroller.querySelectorAll(':scope > slideshow-slide'))
      .filter((slide) => !slide.hidden && slide.getBoundingClientRect().width > 0)
      .map((slide) => Math.max(0, Math.min(max,
        scroller.scrollLeft + slide.getBoundingClientRect().left - left
      )));
  }
  #animateTo(target) {
    const scroller = this.#scroller;
    const start = scroller.scrollLeft;
    const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220;
    let started;
    const frame = (time) => {
      started ??= time;
      const progress = duration ? Math.min(1, (time - started) / duration) : 1;
      const eased = 1 - (1 - progress) ** 3;
      scroller.scrollTo({ left: start + (target - start) * eased, behavior: 'instant' });
      if (progress < 1) {
        this.#animationFrame = requestAnimationFrame(frame);
      } else {
        this.#animationFrame = undefined;
        this.#restoreSnap();
      }
    };
    this.#animationFrame = requestAnimationFrame(frame);
  }
  #restoreSnap() {
    if (this.#scroller) this.#scroller.style.scrollSnapType = this.#originalSnap;
  }
  #stopAnimation() {
    if (this.#animationFrame !== undefined) cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = undefined;
    this.#restoreSnap();
  }
  #selectionHandler = (event) => this.#showSlideBackground(event.detail?.slide);

  connectedCallback() {
    this.#scroller = this.querySelector('.ch-review-carousel__slideshow > slideshow-container > slideshow-slides');
    if (this.#scroller) {
      this.#originalTouchAction = this.#scroller.style.touchAction;
      this.#originalSnap = this.#scroller.style.scrollSnapType;
      this.#scroller.style.touchAction = 'pan-y pinch-zoom';
    }
    this.#scroller?.addEventListener('touchmove', this.#touchMove, { passive: false });
    this.#scroller?.addEventListener('touchstart', this.#touchStart, { passive: true });
    this.#scroller?.addEventListener('touchend', this.#touchEnd, { passive: true });
    this.#scroller?.addEventListener('touchcancel', this.#touchEnd, { passive: true });
    if (this.dataset.syncSectionBackground !== 'true') return;

    const section = this.closest('.section');
    this.#backgroundHost = section?.querySelector(':scope > .custom-section-background');

    if (!this.#backgroundHost) return;

    this.#backgrounds = document.createElement('div');
    this.#backgrounds.className = 'ch-review-carousel__backgrounds';
    this.#backgrounds.setAttribute('aria-hidden', 'true');
    this.#backgrounds.dataset.reviewCarouselOwner = this.getAttribute('data-block-id') || '';
    this.#backgrounds.style.setProperty(
      '--ch-review-transition-duration',
      getComputedStyle(this).getPropertyValue('--ch-review-transition-duration') || '600ms'
    );
    this.#backgrounds.style.setProperty(
      '--ch-review-overlay-color',
      getComputedStyle(this).getPropertyValue('--ch-review-overlay-color') || 'transparent'
    );

    this.#layers = [document.createElement('div'), document.createElement('div')];
    this.#layers.forEach((layer) => {
      layer.className = 'ch-review-carousel__background-layer';
      this.#backgrounds.append(layer);
    });

    this.#backgroundHost.append(this.#backgrounds);
    this.addEventListener('slideshow:select', this.#selectionHandler);

    const initialSlide = this.querySelector('slideshow-slide[aria-hidden="false"]') || this.querySelector('slideshow-slide');
    this.#showSlideBackground(initialSlide, true);
  }

  disconnectedCallback() {
    this.#stopAnimation();
    if (this.#scroller) this.#scroller.style.touchAction = this.#originalTouchAction;
    this.#scroller?.removeEventListener('touchmove', this.#touchMove);
    this.#scroller?.removeEventListener('touchstart', this.#touchStart);
    this.#scroller?.removeEventListener('touchend', this.#touchEnd);
    this.#scroller?.removeEventListener('touchcancel', this.#touchEnd);
    this.#scroller = undefined;
    this.#gesture = undefined;
    this.removeEventListener('slideshow:select', this.#selectionHandler);
    this.#backgrounds?.remove();
    this.#backgroundHost = undefined;
    this.#backgrounds = undefined;
    this.#layers = [];
  }

  #showSlideBackground(slide, immediate = false) {
    if (!(slide instanceof HTMLElement) || this.#layers.length !== 2) return;

    const desktopUrl = slide.dataset.chReviewBackground || '';
    const mobileUrl = slide.dataset.chReviewBackgroundMobile || desktopUrl;
    const desktopPosition = slide.dataset.chReviewBackgroundPosition || 'center center';
    const mobilePosition = slide.dataset.chReviewBackgroundPositionMobile || desktopPosition;

    if (!desktopUrl && !mobileUrl) {
      this.#layers.forEach((layer) => {
        layer.style.opacity = '0';
      });
      return;
    }

    const nextLayerIndex = immediate ? this.#activeLayer : Number(!this.#activeLayer);
    const nextLayer = this.#layers[nextLayerIndex];
    const previousLayer = this.#layers[this.#activeLayer];

    nextLayer.style.setProperty('--ch-review-background-image', this.#cssUrl(desktopUrl || mobileUrl));
    nextLayer.style.setProperty('--ch-review-background-image-mobile', this.#cssUrl(mobileUrl || desktopUrl));
    nextLayer.style.setProperty('--ch-review-background-position', desktopPosition);
    nextLayer.style.setProperty('--ch-review-background-position-mobile', mobilePosition);

    if (immediate) {
      nextLayer.style.transition = 'none';
      nextLayer.style.opacity = '1';
      requestAnimationFrame(() => {
        nextLayer.style.removeProperty('transition');
      });
    } else {
      nextLayer.style.opacity = '1';
      previousLayer.style.opacity = '0';
      this.#activeLayer = nextLayerIndex;
    }
  }

  #cssUrl(url) {
    const escapedUrl = String(url).replaceAll('"', '%22');
    return `url("${escapedUrl}")`;
  }
}

if (!customElements.get('ch-review-carousel')) {
  customElements.define('ch-review-carousel', CampHollowReviewCarousel);
}
